import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/shared/api/api-client';
import { refusalMessage } from '../ambient/decision-identity';
import type {
  AmbientAnalysisPort,
  AnalysisLatestResponse,
  SaveAnalysisBody,
} from '../ambient/analysis-snapshot-types';

// PROD-3195 — the Scrips.Patient ambient analysis endpoints:
//
//   GET  api/Patients/{patientId}/Encounters/{encounterId}/AmbientAnalysis/Latest
//   POST api/Patients/{patientId}/Encounters/{encounterId}/AmbientAnalysis
//
// A save writes the snapshot into the query cache, so a remount (tab switch, navigation) restores from
// what was saved without another read. Errors are NOT caught here: they propagate to the panel, which
// shows them. The analysis is clinical text: it never goes to a log or to Sentry.

const base = (patientId: string, encounterId: string) =>
  `/api/Patients/${encodeURIComponent(patientId)}/Encounters/${encodeURIComponent(encounterId)}/AmbientAnalysis`;

export const ambientAnalysisKey = (patientId?: string | null, encounterId?: string | null) =>
  ['ambient-analysis', patientId, encounterId] as const;

/**
 * The port the ambient panel saves and restores the analysis through. `undefined` until the patient and the
 * encounter are both known: the panel then keeps the analysis in memory for the session only.
 */
export function useAmbientAnalysisPort(
  patientId: string | null | undefined,
  encounterId: string | null | undefined,
): AmbientAnalysisPort | undefined {
  const queryClient = useQueryClient();
  const query = useQuery<AnalysisLatestResponse>({
    queryKey: ambientAnalysisKey(patientId, encounterId),
    enabled: !!patientId && !!encounterId,
    // Only this port changes it: never refetch it behind the panel.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async ({ signal }) =>
      await get<AnalysisLatestResponse>(`${base(patientId as string, encounterId as string)}/Latest`, { signal }),
  });
  const data = query.data;
  const settled = query.isSuccess || query.isError;
  const readError = query.error ? refusalMessage(query.error) : null;

  return useMemo(() => {
    if (!patientId || !encounterId) return undefined;
    return {
      settled,
      snapshot: data?.snapshot ?? null,
      storeAvailable: data?.storeAvailable !== false,
      readError,
      save: async (body: SaveAnalysisBody) => {
        const saved = await post<{ snapshotId: string; createdBy: string; createdAt: string }>(
          base(patientId, encounterId),
          body,
        );
        queryClient.setQueryData<AnalysisLatestResponse>(ambientAnalysisKey(patientId, encounterId), {
          storeAvailable: true,
          snapshot: {
            snapshotId: saved.snapshotId,
            recordingKey: body.recordingKey,
            source: body.source,
            reanalysisSessionId: body.reanalysisSessionId ?? null,
            payloadVersion: body.payloadVersion,
            createdBy: saved.createdBy,
            createdAt: saved.createdAt,
            payload: body.payload,
          },
        });
      },
    };
  }, [patientId, encounterId, settled, data, readError, queryClient]);
}
